/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react";
import { useBankDetailsSchema } from "./useBankDetailsSchema";
import { useBankDetailsErrorsStore } from "./useBankDetailsErrorsStore";
import { z } from "zod";
import type { BankDetails } from "@/types/Payment";

type SchemaKey = keyof z.infer<ReturnType<typeof useBankDetailsSchema>>;

export function useBankDetailsValidation() {
  const schema = useBankDetailsSchema();

  const setErrors = useBankDetailsErrorsStore((state) => state.setErrors);
  const resetErrors = useBankDetailsErrorsStore((state) => state.resetErrors);
  const setIsValidated = useBankDetailsErrorsStore(
    (state) => state.setIsValidated,
  );

  const validate = useCallback(
    (values: BankDetails) => {
      setIsValidated(true);
      try {
        schema.parse(values);
      } catch (error) {
        const errors: Record<string, string> = {};
        if (error instanceof z.ZodError) {
          error.errors.forEach((err) => {
            errors[err.path[0]] = err.message;
          });

          setErrors(errors);
          return errors;
        }
      }

      resetErrors();
      return null;
    },
    [schema],
  );

  const validateField = useCallback(
    (name: SchemaKey, value: string): string | null => {
      const fieldSchema = schema.shape[name];

      if (fieldSchema) {
        try {
          fieldSchema.parse(value);
        } catch (error) {
          if (error instanceof z.ZodError) {
            const message = error.errors[0].message;
            setErrors({ [name]: message });
            return message;
          }
        }
      }

      setErrors({ [name]: null });
      return null;
    },
    [schema],
  );

  return {
    validateField,
    validate,
  };
}
